import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Input,
    Select,
    Stack,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { AdminGuestPlayerResponseDto } from '../../../models/user/dto/admin-guest-player.dto';
import { useCreateGuestPlayer } from '../hooks/useCreateGuestPlayer';
import {
    composeGuestPhoneE164,
    DEFAULT_GUEST_PHONE_CALLING_CODE,
    GUEST_PHONE_COUNTRIES,
    isPlausibleGuestEmail,
} from '../utils/guest-player';

type Props = {
    organizationId: string;
    initialFirstName?: string;
    initialLastName?: string;
    onCancel: () => void;
    // `email` is what the operator typed (trimmed), or '' — it is only ever
    // used for a GUEST, never stored on the player (MYS-140 epic, D4).
    onCreated: (player: AdminGuestPlayerResponseDto, email: string) => void;
};

/**
 * MYS-140 — the "joueur absent de la base" branch of the admin-booking modal.
 * Name + phone are required: the phone is how the API dedupes guests (and
 * finds the app account when the number already belongs to one); the email is
 * optional and only decides whether the payment link is also emailed.
 */
export default function GuestPlayerForm({
    organizationId,
    initialFirstName = '',
    initialLastName = '',
    onCancel,
    onCreated,
}: Props) {
    const [firstName, setFirstName] = useState(initialFirstName);
    const [lastName, setLastName] = useState(initialLastName);
    const [callingCode, setCallingCode] = useState(DEFAULT_GUEST_PHONE_CALLING_CODE);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    const phoneE164 = composeGuestPhoneE164(callingCode, phone);
    const trimmedEmail = email.trim();
    const emailIsValid = trimmedEmail === '' || isPlausibleGuestEmail(trimmedEmail);
    const showPhoneError = phone.trim().length > 0 && !phoneE164;
    const showEmailError = trimmedEmail.length > 0 && !emailIsValid;

    const mutation = useCreateGuestPlayer(organizationId);
    // The inputs are read-only while the request runs: what is sent, and the
    // email handed to `onCreated`, are what the operator validated on click.
    const isSubmitting = mutation.isLoading;

    const canSubmit =
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        Boolean(phoneE164) &&
        emailIsValid &&
        !isSubmitting;

    const submit = () => {
        if (!canSubmit || !phoneE164) return;
        // Pinned at click time: the email handed on is the one that passed
        // `emailIsValid`, whatever the field holds when the reply lands.
        const emailAtSubmit = trimmedEmail;
        mutation.mutate(
            {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phoneNumber: phoneE164,
            },
            { onSuccess: (player) => onCreated(player, emailAtSubmit) },
        );
    };

    return (
        <Box borderWidth={1} borderRadius="md" p={3} data-testid="guest-player-form">
            <Stack spacing={3}>
                <Text fontSize="sm" color="gray.600">
                    Joueur absent de la base : ajoute-le comme invité pour lui envoyer
                    le lien. Il n&apos;a pas besoin de l&apos;app.
                </Text>
                <HStack spacing={3} align="start">
                    <FormControl isRequired>
                        <FormLabel htmlFor="guest-first-name">Prénom</FormLabel>
                        <Input
                            id="guest-first-name"
                            value={firstName}
                            maxLength={100}
                            onChange={(e) => setFirstName(e.target.value)}
                            isDisabled={isSubmitting}
                        />
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel htmlFor="guest-last-name">Nom</FormLabel>
                        <Input
                            id="guest-last-name"
                            value={lastName}
                            maxLength={100}
                            onChange={(e) => setLastName(e.target.value)}
                            isDisabled={isSubmitting}
                        />
                    </FormControl>
                </HStack>
                <FormControl isRequired isInvalid={showPhoneError}>
                    <FormLabel htmlFor="guest-phone">Téléphone</FormLabel>
                    <HStack spacing={2} align="start">
                        <Box minW="170px">
                            <Select
                                aria-label="Indicatif"
                                value={callingCode}
                                onChange={(e) => setCallingCode(e.target.value)}
                                isDisabled={isSubmitting}
                            >
                                {GUEST_PHONE_COUNTRIES.map((country) => (
                                    <option key={country.callingCode} value={country.callingCode}>
                                        {country.label}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Input
                            id="guest-phone"
                            type="tel"
                            placeholder="06 12 34 56 78"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            isDisabled={isSubmitting}
                        />
                    </HStack>
                    <FormHelperText color={showPhoneError ? 'red.500' : undefined}>
                        {showPhoneError
                            ? 'Numéro invalide — vérifie l’indicatif et le numéro.'
                            : 'Sert à retrouver ce joueur la prochaine fois et à lui envoyer le lien par WhatsApp ou SMS.'}
                    </FormHelperText>
                </FormControl>
                <FormControl isInvalid={showEmailError}>
                    <FormLabel htmlFor="guest-email">Email (optionnel)</FormLabel>
                    <Input
                        id="guest-email"
                        type="email"
                        value={email}
                        maxLength={254}
                        onChange={(e) => setEmail(e.target.value)}
                        isDisabled={isSubmitting}
                    />
                    <FormHelperText color={showEmailError ? 'red.500' : undefined}>
                        {showEmailError
                            ? 'Adresse email invalide.'
                            : 'Si renseigné, le lien de paiement lui est aussi envoyé par email.'}
                    </FormHelperText>
                </FormControl>
                <HStack justify="flex-end" spacing={2}>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={onCancel}
                        isDisabled={isSubmitting}
                    >
                        Annuler
                    </Button>
                    <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={submit}
                        isDisabled={!canSubmit}
                        isLoading={isSubmitting}
                    >
                        Ajouter l&apos;invité
                    </Button>
                </HStack>
            </Stack>
        </Box>
    );
}
